import { createFileRoute } from '@tanstack/react-router';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeProvider } from '@mui/material/styles';
import request from '@nineeng/request';
import { useCallback, useEffect, useState } from 'react';
import { userOverviewSchema, UserOverview } from '@repo/shared/domain';
import { EventEmitter, constructQuery } from '@repo/shared/helpers';
import { guardedClient as guardedRequest } from '@repo/shared/helpers';
import CheckinPage from 'src/parts/app/CheckinPage';
import LanguagePage from 'src/parts/app/LanguagePage';
import RegistrationTruckPage from 'src/parts/app/RegistrationTruckPage';
import TasksPage from 'src/parts/app/TasksPage';
import { appTheme } from '@repo/theme';

const AUTO_TIMEOUT = 30 * 60; // 30 minutes

interface SearchParams {
	deviceId: string;
	deviceToken?: string;
	language?: string;
}

export const Route = createFileRoute('/app/registration/fixed')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		deviceId: search.deviceId as string,
		deviceToken: search.deviceToken as string | undefined,
		language: search.language as string | undefined,
	}),
	loaderDeps: ({ search }) => search,
	loader: async ({ deps }) => {
		const { deviceId, deviceToken } = deps;
		const query = constructQuery({ deviceId, deviceToken });
		const { body: device } = await request.get(`/api/devices/${deviceId}?${query}`);

		return {
			auth: query,
			config: device?.config || {},
		};
	},
	component: FixedRegistrationRoute,
});

function FixedRegistrationRoute() {
	const { deviceId, language } = Route.useSearch();
	const { auth, config } = Route.useLoaderData();

	const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

	const [isUploading, setUploading] = useState(false);
	const [registrationData, setRegistrationData] = useState<UserOverview | undefined>();
	const [resultData, setResultData] = useState();
	const [selectedPage, setSelectedPage] = useState(language ? 'registration' : 'language');
	const [lng, setLng] = useState(language ? language : 'EN');

	const roleId = config?.kioskRoleIds?.[0];
	const contactUserId = config?.contactUserId;

	const handleLanguage = (lng: string) => {
		setLng(lng);
		setSelectedPage('registration');
	};

	const handleClose = () => {
		window.parent.postMessage({ close: true }, '*');
	};

	const handleUserReload = useCallback(async () => {
		if (!registrationData?.user?.id) return;

		const { body } = await guardedRequest.get(
			`/api/devices/${deviceId}/user/${registrationData.user.id}/overview?${auth}`,
			{ schema: userOverviewSchema },
		);

		setRegistrationData(body);
		setSelectedPage('tasks');
	}, [auth, deviceId, registrationData?.user?.id]);

	const handleRegistration = async (data: any) => {
		if (isUploading) return;
		setUploading(true);

		try {
			const { body } = await guardedRequest.post(`/api/devices/${deviceId}/registration?${auth}`, {
				...data,
				roleId,
				contactUserId,
			});
			setUploading(false);
			setRegistrationData(body);
			setSelectedPage('tasks');
		} catch (e) {
			console.error(e);
			EventEmitter.emit('toast', { type: 'error', message: 'Error' });
			setUploading(false);
		}
	};

	const handleCheckin = async () => {
		if (!registrationData) return;

		const { body } = await guardedRequest.get(
			`/api/devices/${deviceId}/checkin/${registrationData.user.id}?${auth}&action=checkin`,
		);

		setResultData(body);
		setSelectedPage('checkin');
	};

	const handlePrintBadge = () => {
		const userId = registrationData?.user.id;
		const printUrl = `${baseUrl}/api/devices/${deviceId}/user/badge?userId=${userId}&eventId=`;
		window.parent.postMessage({ printUrl }, '*');
		setTimeout(handleClose, 500);
	};

	useEffect(() => {
		window.parent.postMessage({ isReady: true }, '*');
		setTimeout(handleClose, AUTO_TIMEOUT * 1000);
		handleUserReload();
	}, [handleUserReload]);

	const pages: Record<string, React.ReactNode> = {
		language: <LanguagePage onLanguage={handleLanguage} />,
		registration: <RegistrationTruckPage onSubmit={handleRegistration} lng={lng} />,
		tasks: (
			<TasksPage
				auth={auth}
				user={registrationData?.user}
				tasks={registrationData?.openTasks}
				onReload={handleUserReload}
				onComplete={handleCheckin}
				lng={lng}
			/>
		),
		checkin: <CheckinPage result={resultData} onClose={handleClose} onPrint={handlePrintBadge} lng={lng} />,
	};

	const isConfiguredIncorrectly = !contactUserId || !roleId;

	return (
		<ThemeProvider theme={appTheme}>
			<div className="absolute top-0 bottom-0 left-0 right-0">
				{pages[selectedPage]}

				<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isUploading}>
					<CircularProgress color="inherit" />
				</Backdrop>

				<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isConfiguredIncorrectly}>
					<Alert severity="warning">Invalid configuration, no contact user or no selected role.</Alert>
				</Backdrop>
			</div>
		</ThemeProvider>
	);
}
